// Google OAuth helper functions
// This uses Google Identity Services (GIS) which provides ID tokens directly

declare global {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface Window {
    google: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
          }) => void;
          prompt: () => void;
          renderButton: (element: HTMLElement, config: Record<string, unknown>) => void;
        };
      };
    };
  }
}

let googleInitialized = false;

export const initializeGoogleSignIn = (
  onSuccess: (idToken: string) => void,
  onError?: (error: Error) => void,
): void => {
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  if (!clientId) {
    throw new Error('Google OAuth configuration is missing. Please set REACT_APP_GOOGLE_CLIENT_ID');
  }

  // Load Google Identity Services script if not already loaded
  if (!document.querySelector('script[src*="accounts.google.com/gsi/client"]')) {
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.google?.accounts?.id) {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: (response: { credential: string }) => {
            onSuccess(response.credential);
          },
        });
        googleInitialized = true;
      }
    };
    script.onerror = () => {
      const error = new Error('Failed to load Google Identity Services script');
      if (onError) {
        onError(error);
      } else {
        console.error(error);
      }
    };
    document.head.appendChild(script);
  } else {
    // Script already loaded, just initialize
    if (window.google?.accounts?.id) {
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: (response: { credential: string }) => {
          onSuccess(response.credential);
        },
      });
      googleInitialized = true;
    } else {
      // Wait for script to load
      const checkGoogle = setInterval(() => {
        if (window.google?.accounts?.id) {
          clearInterval(checkGoogle);
          window.google.accounts.id.initialize({
            client_id: clientId,
            callback: (response: { credential: string }) => {
              onSuccess(response.credential);
            },
          });
          googleInitialized = true;
        }
      }, 100);
    }
  }
};

export const triggerGoogleSignIn = (): void => {
  if (!googleInitialized) {
    throw new Error('Google Sign-In not initialized. Call initializeGoogleSignIn first.');
  }

  if (window.google?.accounts?.id) {
    window.google.accounts.id.prompt();
  } else {
    throw new Error('Google Identity Services not available');
  }
};

export const renderGoogleSignInButton = (
  elementId: string,
  onSuccess: (idToken: string) => void,
): void => {
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  if (!clientId) {
    throw new Error('Google OAuth configuration is missing. Please set REACT_APP_GOOGLE_CLIENT_ID');
  }

  // Load Google Identity Services script if not already loaded
  if (!document.querySelector('script[src*="accounts.google.com/gsi/client"]')) {
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.google?.accounts?.id) {
        const element = document.getElementById(elementId);
        if (element) {
          window.google.accounts.id.renderButton(element, {
            type: 'standard',
            theme: 'outline',
            size: 'large',
            text: 'signin_with',
            shape: 'rectangular',
          });

          window.google.accounts.id.initialize({
            client_id: clientId,
            callback: (response: { credential: string }) => {
              onSuccess(response.credential);
            },
          });
          googleInitialized = true;
        }
      }
    };
    document.head.appendChild(script);
  } else {
    // Script already loaded
    if (window.google?.accounts?.id) {
      const element = document.getElementById(elementId);
      if (element) {
        window.google.accounts.id.renderButton(element, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'signin_with',
          shape: 'rectangular',
        });

        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: (response: { credential: string }) => {
            onSuccess(response.credential);
          },
        });
        googleInitialized = true;
      }
    }
  }
};
